import { SetStateAction, useCallback, useMemo } from 'react';
import { useAtom } from 'jotai';

import { LocalStorage } from '@flipster/utils/localStorage';

import { isSetStateFunction } from './is-set-state-function';
import { createPersistStateAtom, PersistStateAtom, PersistStateOptions } from './persist-state.atom';
import { PersistStateLocalStorageDelegate } from './persist-state.delegate';

type LocalStorageKey = Extract<keyof LocalStorage, string>;
interface UsePersistStateOptions<K extends LocalStorageKey>
  extends Omit<PersistStateOptions<LocalStorage, K>, 'delegate'> {
  key: K;
}

const localStorageAtomCache: Record<string, PersistStateAtom<LocalStorage, keyof LocalStorage>> = {};
export function getLocalStorageAtom<K extends LocalStorageKey>(key: K, defaultValue: LocalStorage[K]) {
  localStorageAtomCache[key] ??= createPersistStateAtom(key, {
    delegate: PersistStateLocalStorageDelegate,
    defaultValue,
  }) as PersistStateAtom<LocalStorage, keyof LocalStorage>;
  return localStorageAtomCache[key] as PersistStateAtom<LocalStorage, K>;
}

export function useLocalStorage<K extends Extract<keyof LocalStorage, string>>({
  key,
  defaultValue,
}: UsePersistStateOptions<K>) {
  const stateAtom = getLocalStorageAtom(key, defaultValue);
  const [value, setValue] = useAtom(stateAtom);

  const setValueInternal = useCallback(
    (incoming: SetStateAction<LocalStorage[K]>) => {
      setValue((prev) => (isSetStateFunction(incoming) ? incoming(prev) : incoming));
    },
    [setValue],
  );

  return useMemo(() => [value, setValueInternal] as const, [setValueInternal, value]);
}
